import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UTCDate } from 'map-wald';
import { ChartComponent, ChartEntry, ChartSeries } from '../chart/chart.component';
import {groupBy} from 'ramda';
import * as FileSaver from 'file-saver';
import * as R from 'ramda';
import { TimePeriod } from '../data';
import { TimePeriodUtils } from '../utils';

const COLOUR_RANGE='rgb(198,219,239)';
const COLOR_MEAN='rgb(39,135,217)';
const ANNUAL_BAR_PREV='rgb(143,170,220)';
const ANNUAL_BAR_CURR='rgb(47,85,151)';

const WEIGHT_SCALE_NORMAL=1.0;
const WEIGHT_SCALE_EXPANDED=1.25;
const WEIGHT_SCALE_IMAGE=1.75
export const CM_NORMAL='Time series';
export const CM_ANNUAL='Annual time series';
export const CM_ANNUAL_FILTERED='Annual time series (selected months)';
export const CM_DEVIATION='Deviation from monthly mean';
export const CM_DEVIATION_ANNUAL_PC='Percentage deviation from annual mean';
export const CM_YR_ON_YR='Year on year';
export const CM_YR_ON_YR_CUMUL='Year on year (cumulative)';
const X_AXIS_DEFAULTS = {
  tickformat:null as string,
  nticks:null as number,
  ticklabelmode:null as string,
  range:null as [any,any]
};

@Component({
  selector: 'app-multi-year-timeseries-chart',
  templateUrl: './multi-year-timeseries-chart.component.html',
  styleUrls: ['./multi-year-timeseries-chart.component.scss']
})
export class MultiYearTimeseriesChartComponent implements OnInit, OnChanges {
  @Input() chartSeries: ChartSeries;
  @Input() chartLabel = '';
  @Input() focusYear = -1;
  @Input() timePeriod: TimePeriod;
  @Input() annualTransform = 'mean';
  @Input() chartHeight = 200;
  @Input() expandable = true;
  @Input() downloadTimestep: string;

  @ViewChild('chart') chart: ChartComponent;
  @Output() chartCleared = new EventEmitter<void>();
  @Output() chartExpanded = new EventEmitter<void>();
  axisLabel='';
  @Input() chartMode = CM_YR_ON_YR;
  // @Output() chartModeChange = new EventEmitter<string>();
  xAxis = {
    ...X_AXIS_DEFAULTS
  };
  yAxis = {
    range:null as [number,number]
  };
  showLegend = false;
  effectiveChartSeries: ChartSeries[] = [];
  weightScale = WEIGHT_SCALE_NORMAL;

  constructor() { }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes.chartSeries||changes.focusYear||changes.chartMode||this.expandable){
      this.configureChartMode();
    }
  }

  ngOnInit(): void {
  }

  configureChartMode(): void {
    this.xAxis = {...X_AXIS_DEFAULTS};
    this.yAxis.range = null;
    this.weightScale = this.weightScaleToUse();
    this.showLegend = false;
    this.axisLabel=this.chartSeries?.title||'';
    if(this.chartSeries?.units){
      if(this.axisLabel){
        this.axisLabel += ` (${this.chartSeries.units})`;
      } else {
        this.axisLabel = this.chartSeries.units;
      }
    }

    if(!this.chartSeries?.data?.length){
      this.effectiveChartSeries = [];
      return;
    }

    let dynamicYAxis = false;

    switch(this.chartMode){
      case CM_NORMAL:
        this.effectiveChartSeries = [
          {
            ...this.chartSeries,
            line: {
              width: 1
            }
          }
        ];
        break;
      case CM_DEVIATION:
        this.effectiveChartSeries = [
          {
            title: this.chartSeries.title,
            units: this.chartSeries.units,
            data: convertToMonthlyVariance(this.chartSeries.data),
            line: {
              width: 1
            }
          }
        ];
        break;
      case CM_ANNUAL:
      case CM_DEVIATION_ANNUAL_PC:
      case CM_ANNUAL_FILTERED:
        const annual = this.chartMode===CM_ANNUAL_FILTERED ? 
          convertToFilteredAnnual(this.chartSeries.data,this.annualTransform,this.timePeriod) :
          convertToAnnual(this.chartSeries.data,this.annualTransform)
        let data = annual;
        let units = this.chartSeries.units;
        if(this.chartMode===CM_DEVIATION_ANNUAL_PC){
          const annualMean = annual.reduce((sum,r)=>sum+r.value,0)/annual.length;
          data = annual.map(r=>{
            const val = 100.0*(r.value-annualMean)/annualMean;
            return {
              date:r.date,
              value:val
            };
          });
          units = '%';
          const start = annual[0].date;
          const end = annual[annual.length-1].date;
          this.axisLabel = `Difference from ${start}-${end} mean (%)`;
        } else {
          dynamicYAxis = true;
        }

        this.effectiveChartSeries = [
          {
            title: this.chartSeries.title,
            units: units,
            type: 'bar',
            data: data,
            colour: data.map((_,i)=>(i<data.length-1)?ANNUAL_BAR_PREV:ANNUAL_BAR_CURR)
          }
        ];
        const count = this.effectiveChartSeries[0].data.length;
        this.effectiveChartSeries[0].data[count-1].text='ytd';
        break;
      case CM_YR_ON_YR:
      case CM_YR_ON_YR_CUMUL:
        this.effectiveChartSeries = makeYearOnYear(toMonthly(this.chartSeries),this.focusYear,this.chartMode===CM_YR_ON_YR_CUMUL);
        this.xAxis.tickformat = '%b';
        this.xAxis.nticks = 12;

        this.xAxis.range = yearlyChartRange(this.effectiveChartSeries[this.effectiveChartSeries.length-1]);
        this.xAxis.ticklabelmode = 'period';
        this.showLegend = true;
        dynamicYAxis = true;
        break;
    }

    if(dynamicYAxis){
      this.yAxis.range = this.effectiveChartSeries.map(s=>{
        // Range of each series
        const vals = s.data.map(r=>r.value);
        return [Math.min(...vals),Math.max(...vals)];
      }).map(r=>{
        let result = [r[0]-0.1*(r[1]-r[0]),r[1]+0.1*(r[1]-r[0])]; // Expanded range of each series
        if((r[0]>=0) && result[0] < 0){
          result[0] = 0;
        }
        return result;
      }).reduce((lhs,rhs)=>[Math.min(lhs[0],rhs[0]),Math.max(lhs[1],rhs[1])]) as [number,number]; // Overall range
    }
  }

  downloadClick(): void {
    this.downloadData();
  }

  downloadData(): void {
    let fileName = `GWM-${this.chartSeries.title}-${this.chartLabel}.csv`;
    fileName = fileName.replace(/[ \(\)\,]/g,'-').replace(/--+/g,'-').replace('-.csv','.csv');

    const output = new Blob(
      [this.makeCSV()],
      {type: 'text/plain;charset=utf-8'});
    FileSaver.saveAs(output, fileName);
  }

  clearTimeSeries(): void {
    this.chartCleared.emit();
    // this.chartSeries = null;
    // this.configureChartMode();
  }

  expand(): void {
    this.chartExpanded.emit();
  }

  makeCSV(): string {
    const res = ([] as string[]).concat(
      ['date,value'],
      this.chartSeries.data.map(r=>`${formatDateForCSV(r.date as Date,this.downloadTimestep)},${r.value}`))
    return res.join('\n');
  }

  downloadImage(): void {
    this.weightScale = WEIGHT_SCALE_IMAGE;
    setTimeout(()=>{
      this.chart.downloadImage(this.chartLabel);
      this.weightScale = this.weightScaleToUse();
    });
  }

  weightScaleToUse(): number {
    return this.expandable ? WEIGHT_SCALE_NORMAL : WEIGHT_SCALE_EXPANDED;
  }
}

function toCumulative(table:ChartEntry[]):ChartEntry[] {
  let sum = 0.0;
  return table.map(row=>{
    sum += row.value;
    return {
      date:row.date,
      value:sum
    };
  });
}

function convertToMonthlyVariance(chartData: ChartEntry[]): ChartEntry[] {
  const monthlyTotals = new Array<number>(12).fill(0);
  const monthlyCounts = new Array<number>(12).fill(0);
  chartData.forEach(row=>{
    const month = (row.date as UTCDate).getUTCMonth();
    monthlyTotals[month] += row.value;
    monthlyCounts[month]++;
  });
  const monthlyMeans = monthlyCounts.map((c,i)=>(c?(monthlyTotals[i]/c):NaN));
  return chartData.map(row=>{
    return {
      date:row.date,
      value:row.value - monthlyMeans[(row.date as UTCDate).getUTCMonth()]
    };
  });
}


function convertToAnnual(chartData: ChartEntry[],transform: string): ChartEntry[] {
  transform = (transform || 'mean').toLowerCase();

  const annualMetric:{[key:number]:number} = {};
  const annualCounts:{[key:number]:number} = {};
  chartData.forEach(e=>{
    const year = e.date.getUTCFullYear();
    if(!annualMetric[year]){
      annualMetric[year]=e.value;
    } else {
      if(transform==='min'){
        annualMetric[year] = Math.min(annualMetric[year],e.value);
      } else if(transform==='max'){
        annualMetric[year] = Math.max(annualMetric[year],e.value);
      } else {
        annualMetric[year] += e.value;
      }
    }
    annualCounts[year]=(annualCounts[year]||0)+1;
  });

  return Object.keys(annualMetric).sort().map(year=>{
    const entry:ChartEntry = {
      date: (+year as any) as UTCDate,
      value: (transform==='mean') ? (annualMetric[+year]/annualCounts[+year]) : annualMetric[+year]
    };
    return entry;
  });
}

function convertToFilteredAnnual(chartData: ChartEntry[],transform: string,timePeriod: TimePeriod): ChartEntry[] {
  transform = (transform || 'mean').toLowerCase();

  const timePeriods = TimePeriodUtils.makeLikeTimePeriods(timePeriod,chartData[0].date,chartData[chartData.length-1].date);
  timePeriods.push(timePeriod);
  let currentPeriodIdx = 0;
  const annualMetric:{[key:number]:number} = {};
  const annualCounts:{[key:number]:number} = {};

  chartData.forEach(e=>{
    const date = e.date as UTCDate;
    let keep=false;
    if(TimePeriodUtils.withinTimePeriod(date,timePeriods[currentPeriodIdx])){
      keep = true;
    } else if(timePeriods[currentPeriodIdx+1]&&TimePeriodUtils.withinTimePeriod(date,timePeriods[currentPeriodIdx+1])){
      currentPeriodIdx++;
      keep = true;
    }

    if(keep){
      const year = timePeriods[currentPeriodIdx].to.getUTCFullYear();
      if(!annualMetric[year]){
        annualMetric[year]=e.value;
      } else {
        if(transform==='min'){
          annualMetric[year] = Math.min(annualMetric[year],e.value);
        } else if(transform==='max'){
          annualMetric[year] = Math.max(annualMetric[year],e.value);
        } else {
          annualMetric[year] += e.value;
        }
      }
      annualCounts[year]=(annualCounts[year]||0)+1;
    }
  });
  return Object.keys(annualMetric).sort().map(year=>{
    const entry:ChartEntry = {
      date: (+year as any) as UTCDate,
      value: (transform==='mean') ? (annualMetric[+year]/annualCounts[+year]) : annualMetric[+year]
    };
    return entry;
  });
}

function formatDateForCSV(d:Date,timestep:string):string {
  let result = `${d.getUTCFullYear()}-${pad(d.getUTCMonth()+1)}`;
  if(timestep.toLowerCase()==='monthly'){
    return result;
  }
  return `${result}-${pad(d.getUTCDate())}`
}

function pad(n:number):string {
  return n<10?'0'+n:n.toString();
}

function makeYearOnYear(chartSeries:ChartSeries,focusYear:number,cumulative:boolean):ChartSeries[]{
  const orig = chartSeries.data;
  const groups = groupBy(r=>(r.date as UTCDate).getUTCFullYear().toString(),orig);
  const years = Object.keys(groups).map(yr=>+yr).sort().reverse();
  const maxYear = years[0];
  const individualYears = years.map((yr,i)=>{
    let shifted:ChartEntry[] = groups[yr.toString()].map(r=>{
      const d = new Date(r.date as Date);
      d.setUTCFullYear(maxYear);
      d.setUTCDate(15);
      return {
        date:d,
        value:r.value
      };
    });
    if(cumulative){
      shifted = toCumulative(shifted/*.reverse()*/);
    }
    return shifted;
  });

  const idxOfFocusYear = years.indexOf(focusYear);
  const statisticAcrossYears = (fn:((...arr:number[])=>number)) => {
    const repYearOfData = individualYears[1];
    return repYearOfData.map((_,i)=>{
      const vals = individualYears
      .filter((_,yrIdx)=>yrIdx!==idxOfFocusYear)
      .filter(yr=>yr.length===12)
      .map(y=>y[i]?.value).filter(v=>typeof v === 'number');
      return {
        date:repYearOfData[i].date,
        value:fn(...vals)
      };
    });
  }
  const minValues = statisticAcrossYears(Math.min);
  const maxValues = statisticAcrossYears(Math.max);
  const meanValues = statisticAcrossYears((...arr:number[])=>arr.reduce((sum,v)=>sum+v,0)/arr.length);
  let focusData = individualYears[idxOfFocusYear];
  if(!focusData){
    focusData = individualYears[0].map(r=>({
      date:r.date,
      value:NaN
    }));
  }
  const result = [
    {
      title: chartSeries.title,
      units: chartSeries.units,
      data: focusData,
      colour:'red',
      mode:'lines',
      hoverinfo:'y',
    },
    {
      title: 'Mean',
      units: chartSeries.units,
      data: meanValues,
      colour:COLOR_MEAN,
      line: {
        dash: 'dash'
      },
      mode:'lines',
      hoverinfo:'y',
    },
    {
      title: 'Historical range',
      units: chartSeries.units,
      data: maxValues,
      colour:COLOUR_RANGE,
      mode:'lines',
      fill:'tonexty',
      fillcolor: COLOUR_RANGE,
      hoverinfo:'y',
    },
    {
      units: chartSeries.units,
      data: minValues,
      colour:COLOUR_RANGE,
      mode:'lines',
      fill:'tozeroy',
      fillcolor: 'white',
      hoverinfo:'y',
      legend: false
    },
  ];

  if(idxOfFocusYear<(years.length-1)){
    result.splice(1,0,{
      title: 'Previous year',
      units: chartSeries.units,
      data: individualYears[idxOfFocusYear+1],
      colour:'yellow',
      mode:'lines',
      hoverinfo:'y',
    });
  }

  return result;
}

function yearlyChartRange(series:ChartSeries):[Date,Date]{
  const dates = series.data.map(r=>r.date);
  const start = new Date(dates[0].getTime());
  const end = new Date(dates[dates.length-1].getTime());
  start.setUTCDate(start.getUTCDate()-15);
  end.setUTCDate(end.getUTCDate()+15);
  // start.setUTCMonth(start.getUTCMonth()-1);
  // end.setUTCMonth(end.getUTCMonth()+1);
  return [start,end];
}
function toMonthly(chartSeries: ChartSeries): ChartSeries {
  // Convert a possibly sub-monthly series to monthly taking the average of all entries within a month/year
  const groups =
    groupBy(r=>(r.date as UTCDate).getUTCFullYear().toString()+(r.date as UTCDate).getUTCMonth().toString(),chartSeries.data);
  const monthly = Object.keys(groups).map(k=>{
    const rows = groups[k];
    const date = new Date(rows[0].date.getTime());
    date.setUTCDate(1);
    const value = rows.reduce((sum,r)=>sum+r.value,0)/rows.length;
    return {
      date,
      value
    };
  });
  return {
    ...chartSeries,
    data: monthly
  };
}

